import { defaultComparators } from './default-comparators.js';
import { normalizeThresholdRules } from './normalize-rules.js';
import type {
  DataTableThresholdsNormalizedRule,
  DataTableThresholdProps,
} from './threshold-types.js';
import { useDataTableConfigElement } from '../../../hooks/useDataTableConfig.js';
import {
  isAccessorFnColumnDef,
  isAccessorKeyColumnDef,
  isGroupColumnDef,
} from '../../hooks/useTable/datatable-typeguards.js';
import {
  type Cell,
  type Column,
  type Row,
  type RowData,
  type Table,
  type TableFeature,
  type TableOptions,
} from '../../hooks/useTable/types.js';
import {
  assignRowMethods,
  getMemoOptions,
} from '../../hooks/useTable/utils.js';
import type { DataTableRowData } from '../../public.api.js';
import { memo } from '../../tanstack-table-core-override/src/index.js';

/**
 * Hook configuring the thresholds feature for the DataTable.
 * @internal
 */
export function useThresholds<TData>(
  props: DataTableThresholdProps<TData>,
  options: TableOptions<TData>,
): void {
  const configThresholds = useDataTableConfigElement('rowThresholds');

  // check if either the table or any columns have thresholds
  const rowThresholds = props.rowThresholds ?? configThresholds;
  // First check if rowThresholds is defined, because this is the cheaper check
  if (rowThresholds) {
    options.rowThresholds = normalizeThresholdRules(rowThresholds);
    options.hasThresholds = true;
  } else {
    options.rowThresholds = undefined;
    // Check through the columnDefs if there are thresholds defined.
    options.hasThresholds = options.columns.some((column) => {
      if (isGroupColumnDef(column)) {
        const groupedColumn = column;
        return (
          groupedColumn.columns &&
          groupedColumn.columns.some(
            (childColumn) =>
              childColumn.thresholds && childColumn.thresholds.length > 0,
          )
        );
      }
      return column.thresholds && column.thresholds.length > 0;
    });
  }
}

/**
 * Helper function that will check if all rules of a given threshold match.
 * @internal
 */
function matchesThreshold<TData extends DataTableRowData>(
  threshold: DataTableThresholdsNormalizedRule<TData>,
  rowData: TData,
): boolean {
  return threshold.rules.every((rule) => {
    const compareValue = rule.accessor?.(rowData) ?? undefined;
    if (typeof rule.comparator === 'string') {
      return defaultComparators[rule.comparator](rule.value, compareValue);
    }
    return rule.comparator(rowData);
  });
}

/**
 * Feature to handle thresholds to help in row and cell highlighting.
 * @internal
 */
export const Thresholds: TableFeature = {
  createTable<TData extends DataTableRowData>(table: Table<TData>) {
    assignRowMethods(table, {
      getThresholdPill: memo(
        function (this: Row<TData>) {
          return [table.options.rowThresholds, this.id, this.index];
        },
        function (
          this: Row<TData>,
          rowThresholds: DataTableThresholdsNormalizedRule<TData>[] | undefined,
        ) {
          if (rowThresholds === undefined || rowThresholds.length === 0) {
            return null;
          }

          // Check from the back, because the last one matching one should be
          // the one applying, if there are more than one rules applied.
          const pillColor = rowThresholds.findLast(
            (threshold) =>
              threshold.type === 'pill' &&
              matchesThreshold(threshold, this.original),
          )?.color;

          return pillColor ?? null;
        },
        getMemoOptions(table.options, 'debugRows', 'row.getThresholdPill'),
      ),

      getThresholdColors: memo(
        function (this: Row<TData>) {
          return [table.options.rowThresholds, this.id, this.index];
        },
        function (
          this: Row<TData>,
          rowThresholds: DataTableThresholdsNormalizedRule<TData>[] | undefined,
        ) {
          if (rowThresholds === undefined || rowThresholds.length === 0) {
            return null;
          }

          // Check from the back, because the last one matching one should be
          // the one applying, if there are more than one rules applied.
          const matchedThreshold = rowThresholds.findLast(
            (threshold) =>
              threshold.type === 'highlight' &&
              matchesThreshold(threshold, this.original),
          );

          if (matchedThreshold) {
            return {
              color: matchedThreshold.color,
              backgroundColor: matchedThreshold.backgroundColor,
            };
          }
          return null;
        },
        getMemoOptions(table.options, 'debugRows', 'row.getThresholdColors'),
      ),
    });
  },

  createColumn<TData extends DataTableRowData>(
    column: Column<TData>,
    table: Table<TData>,
  ) {
    column.getThresholdRules = memo(
      () => [column.columnDef.thresholds],
      (thresholds) => {
        if (thresholds === undefined || thresholds.length === 0) {
          return null;
        }

        const columnDef = column.columnDef;

        // Calculate the fallback accessor that should be used if there is
        // non given on the threshold rule.
        let fallbackAccessor: string | ((row: TData) => unknown) | undefined;
        if (isAccessorKeyColumnDef(columnDef)) {
          fallbackAccessor = columnDef.accessorKey;
        }
        if (isAccessorFnColumnDef(columnDef)) {
          const accessorFn = columnDef.accessorFn;
          fallbackAccessor = (row: TData) => accessorFn(row, 0);
        }

        return normalizeThresholdRules(thresholds, fallbackAccessor);
      },
      getMemoOptions(table.options, 'debugColumns', 'column.getThresholdRules'),
    );
  },

  createCell<TData extends RowData, TValue>(
    cell: Cell<TData, TValue>,
    column: Column<TData, TValue>,
    row: Row<TData>,
  ) {
    cell.getThresholdColors = () => {
      const columnThresholds = column.getThresholdRules();

      const matchedColors: { color?: string; backgroundColor?: string } = {
        color: undefined,
        backgroundColor: undefined,
      };

      if (columnThresholds) {
        // Check from the back, because the last one matching one should be
        // the one applying, if there are more than one rules applied.
        const matchedColumnThreshold = columnThresholds.findLast((threshold) =>
          matchesThreshold(threshold, row.original),
        );

        if (matchedColumnThreshold) {
          matchedColors.color = matchedColumnThreshold.color;
          matchedColors.backgroundColor =
            matchedColumnThreshold.backgroundColor;
          return matchedColors;
        }
      }

      // If no column threshold rules matched, we can check if the row threshold
      // rule will match.
      const rowThresholds = row.getThresholdColors();
      if (rowThresholds) {
        matchedColors.color = rowThresholds.color;
        matchedColors.backgroundColor = rowThresholds.backgroundColor;
      }
      return matchedColors;
    };
  },
};
